import React, { FunctionComponent, useState } from "react"
import { Grid, TextField, Select, MenuItem } from "@material-ui/core"
import { useQuery } from "@apollo/client"
import { GET_INIT_UPLOAD_INFO } from "../../../queries/GET_INIT_UPLOAD_INFO"
import { AudioFormat, Language, UploadAudioInput } from "../../../../typings/types"
import { generateId } from "../../../common/idGenerator"
import styles from "./UploadForm.module.scss"
import { FieldContainer } from "../../../components/FormHelpers/FieldContainer"
import { useUploadFormValidation } from "./validation/useUploadFormValidation"
import { FileUploadField } from "../../../components/FileUploadField/FileUploadField"
interface UploadFormProps {
    onSubmit: (values: UploadAudioInput) => void
}
export const UploadForm: FunctionComponent<UploadFormProps> = ({ onSubmit }) => {
    const [availableLanguages, setAvailableLanguages] = useState<Language[]>([])
    const [availableAudioFormats, setAvailableAudioFormats] = useState<AudioFormat[]>([])
    const {
        values,
        errors,
        handleChange,
        handleBlur,
        audioFileHandleChange,
        handleSubmit
    } = useUploadFormValidation({
        onValidSubmit: () => onSubmit(composeUploadAudioInput())
    })

    useQuery(GET_INIT_UPLOAD_INFO, {
        onCompleted: data => {
            if (data.availableLanguages.length) setAvailableLanguages(data.availableLanguages)
            if (data.getAudioFormats.length) setAvailableAudioFormats(data.getAudioFormats)
        }
    })

    const composeAudioFileAcceptTypes = () => {
        if (availableAudioFormats.length) {
            return Array.from(new Set(availableAudioFormats.map(format => `.${format.type}`)))
        } else return []
    }

    const composeUploadAudioInput = (): UploadAudioInput => ({
        name: values.name,
        referenceId: generateId(),
        audioFormatId: values.audioFormat,
        languageIds: values.languageIds,
        text: values.text,
        base64: values.audioFile
    })

    return (
        <form onSubmit={handleSubmit} id="upload-audio-form">
            <Grid container direction="row" spacing={2} data-testid="upload-form">
                <Grid item container direction="column" spacing={1}>
                    <Grid item>
                        <FieldContainer
                            className={styles.UploadForm__fileInput}
                            label="File"
                            error={errors.audioFile}
                        >
                            <FileUploadField
                                acceptTypes={composeAudioFileAcceptTypes()}
                                onUpload={audioFileHandleChange}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item>
                        <FieldContainer
                            label="Name"
                            info="Required. The name of this audio. Please don't include file type extensions."
                            error={errors.name}
                        >
                            <TextField
                                name="name"
                                value={values.name}
                                required
                                inputProps={{
                                    "data-testid": "prompt-name-input-field"
                                }}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!errors.name}
                                size="small"
                                variant="outlined"
                                fullWidth
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item>
                        <FieldContainer label="Format" error={errors.audioFormat}>
                            <Select
                                name="audioFormat"
                                value={values.audioFormat}
                                onChange={handleChange}
                                error={!!errors.audioFormat}
                                data-testid="audio-formats-input-field"
                            >
                                {availableAudioFormats.map(format => (
                                    <MenuItem key={format.id} value={format.id}>
                                        {`${format.name} (${format.encoding})`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FieldContainer>
                    </Grid>
                    <Grid item>
                        <FieldContainer label="Languages">
                            <Select
                                name="languageIds"
                                data-testid="languages-input-field"
                                multiple
                                value={values.languageIds}
                                onChange={handleChange}
                            >
                                {availableLanguages.map(language => (
                                    <MenuItem
                                        classes={{
                                            selected: styles.UploadForm__languageSelected
                                        }}
                                        key={language.id}
                                        value={language.id}
                                        data-testid={`upload-language-option-${language.name}`}
                                    >
                                        {language.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FieldContainer>
                    </Grid>
                    <Grid item>
                        <FieldContainer
                            label="Text"
                            info="Optional. The text spoken in this audio."
                        >
                            <TextField
                                name="text"
                                data-testid="prompt-text-input-field"
                                onChange={handleChange}
                                value={values.text}
                                fullWidth
                                rows={7}
                                variant="outlined"
                                multiline
                            />
                        </FieldContainer>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    )
}
